"use client";

import { useState, useCallback, useRef } from "react";
import { Search, X, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { DataTableConfig } from "./types";

interface DataTableToolbarProps {
  config: DataTableConfig;
  onSearch: (field: string, value: string) => void;
  onFilter: (field: string, value: string) => void;
  onDateFilter?: (field: string, start?: string, end?: string) => void;
  onReset?: () => void;
}

export function DataTableToolbar({
  config,
  onSearch,
  onFilter,
  onDateFilter,
  onReset,
}: DataTableToolbarProps) {
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  // U-04: Date range state — tracks start/end values per field
  const [dateValues, setDateValues] = useState<Record<string, { start: string; end: string }>>({});

  const handleSearch = useCallback(
    (field: string, value: string) => {
      setSearchValues((prev) => ({ ...prev, [field]: value }));

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        onSearch(field, value);
      }, 500);
    },
    [onSearch]
  );
  const handleReset = () => {
    setSearchValues({});
    setFilterValues({});
    setDateValues({});

    if (onReset) {
      onReset();
    } else {
      // Notify parent to clear all filters
      config.searchFields?.forEach(s => onSearch(s.field, ""));
      config.filterFields?.forEach(f => onFilter(f.field, ""));
      config.dateFields?.forEach(d => onDateFilter?.(d.field, undefined, undefined));
    }
  };

  const handleApply = () => {
    Object.entries(searchValues).forEach(([field, value]) => onSearch(field, value));
    Object.entries(filterValues).forEach(([field, value]) => onFilter(field, value));
    Object.entries(dateValues).forEach(([field, { start, end }]) =>
      onDateFilter?.(field, start || undefined, end || undefined)
    );
  };

  return (
    <div className="flex flex-col gap-6 bg-white/[0.02] p-4 rounded-xl border border-white/5 mb-6">
      {/* ── Top row: Title and Custom Actions ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {config.title && (
            <div className="flex items-center gap-3">
              <div className="h-5 w-1 rounded-full" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
              <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/80">{config.title}</h2>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {config.customButtons?.map((btn) => (
            <Button
              key={btn.label}
              variant={btn.variant || "default"}
              size="sm"
              onClick={btn.onClick}
              className={btn.label.includes("Add") ? "btn-orange" : ""}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>

      {/* ── Filter/Search row: Grouped and Center Aligned ── */}
      <div className="flex flex-col items-center gap-5 pt-2 border-t border-white/5">
        <div className="flex flex-wrap items-center justify-center gap-3 w-full">
          {config.searchFields?.map((search) => (
            <div key={search.field} className="relative">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder={search.placeholder || `Search ${search.label}...`}
                value={searchValues[search.field] || ""}
                onChange={(e) => setSearchValues(prev => ({ ...prev, [search.field]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && handleApply()}
                className="h-9 w-full min-w-[200px] max-w-[260px] pl-8 bg-black/20 border-white/10"
              />
            </div>
          ))}

          {config.filterFields?.map((filter) => (
            <Select
              key={filter.field}
              value={filterValues[filter.field] || "__all__"}
              onValueChange={(value) => setFilterValues(prev => ({ ...prev, [filter.field]: value === "__all__" ? "" : value }))}
            >
              <SelectTrigger className="h-9 w-[180px] bg-black/20 border-white/10">
                <SelectValue placeholder={filter.label} />
              </SelectTrigger>
              <SelectContent className="bg-[#12121e] border-white/10">
                <SelectItem value="__all__">All {filter.label}</SelectItem>
                {filter.values.map((v) => (
                  <SelectItem key={v.val} value={v.val}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}

          {/* Date range filter fields */}
          {config.dateFields?.map((dateField) => {
            const vals = dateValues[dateField.field] ?? { start: "", end: "" };
            return (
              <div key={dateField.field} className="flex items-center gap-1.5 px-3 py-1 bg-black/20 rounded-md border border-white/10">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">{dateField.startLabel}:</span>
                <input
                  type="date"
                  value={vals.start}
                  onChange={(e) => setDateValues(prev => ({ ...prev, [dateField.field]: { ...vals, start: e.target.value } }))}
                  className="h-7 bg-transparent border-none text-xs text-foreground focus:ring-0 outline-none"
                />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0 border-l border-white/10 pl-1.5 ml-1">{dateField.endLabel}:</span>
                <input
                  type="date"
                  value={vals.end}
                  onChange={(e) => setDateValues(prev => ({ ...prev, [dateField.field]: { ...vals, end: e.target.value } }))}
                  className="h-7 bg-transparent border-none text-xs text-foreground focus:ring-0 outline-none"
                />
              </div>
            );
          })}
        </div>

        {/* ── Search/Reset Buttons (Centered of the row) ── */}
        <div className="flex items-center justify-center gap-4 w-full">
          <Button
            size="icon"
            onClick={handleApply}
            title="Apply filters"
            className="btn-orange h-10 w-10 shadow-lg shadow-orange-500/20"
          >
            <Search className="size-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleReset}
            title="Reset filters"
            className="h-10 w-10 text-muted-foreground hover:text-foreground hover:bg-white/5"
          >
            <RotateCcw className="size-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
